// src/context/AISettingsContext.tsx
import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { AISettingsState } from '../components/AISettings';

const DEFAULT_AI_SETTINGS: AISettingsState = {
  difficulty: 'medium',
  materialBalance: 0.40,
  piecePositioning: 0.25,
  kingSafety: 0.20,
  mobility: 0.15,
  pawnStructure: false,
  boardControl: false,
  priorityOrder: ['checkmate', 'capture_high', 'check', 'exchange_favorable', 'fork', 'threat', 'development', 'castling'],
  maxThinkingTime: 30,
  useIterativeDeepening: true,
  useAlphaBetaPruning: true,
  threadCount: 20,  // Default to 20 threads for native Rust engine
};

// Helper function to load AI settings from localStorage
const loadAISettings = (): AISettingsState => {
  try {
    const saved = localStorage.getItem('aiSettings');
    if (saved) {
      const parsed = JSON.parse(saved);
      return { ...DEFAULT_AI_SETTINGS, ...parsed };
    }
  } catch (error) {
    console.error('Failed to load AI settings from localStorage:', error);
  }
  return DEFAULT_AI_SETTINGS;
};

interface AISettingsContextType {
  aiSettings: AISettingsState;
  updateAISettings: (settings: AISettingsState) => void;
}

const AISettingsContext = createContext<AISettingsContextType | null>(null);

export const AISettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Use a ref to store the actual settings to avoid re-renders
  const settingsRef = useRef<AISettingsState>(loadAISettings());
  
  // This state is only used to trigger re-renders when settings are explicitly updated
  const [version, setVersion] = useState(0);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = loadAISettings();
    settingsRef.current = saved;
  }, []);

  const updateAISettings = (newSettings: AISettingsState) => {
    settingsRef.current = newSettings;
    localStorage.setItem('aiSettings', JSON.stringify(newSettings));
    console.log('[AISettingsContext] Settings updated:', newSettings.difficulty);
    // Increment version to trigger re-render only in components that need it
    setVersion(v => v + 1);
  };

  // Create a stable context value
  const contextValue: AISettingsContextType = {
    aiSettings: settingsRef.current,
    updateAISettings,
  };

  return (
    <AISettingsContext.Provider value={contextValue}>
      {children}
    </AISettingsContext.Provider>
  );
};

export const useAISettings = (): AISettingsContextType => {
  const context = useContext(AISettingsContext);
  if (!context) {
    throw new Error('useAISettings must be used within an AISettingsProvider');
  }
  return context;
};

export { DEFAULT_AI_SETTINGS };
